import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { useDataCache } from '../context/DataCacheContext';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'usePerformanceReview' });

interface PerformanceCompetency {
  id: string;
  focus_id: string;
  name: string;
  description: string;
  weight: number;
  order_index: number;
  is_custom: boolean;
  template_id?: string;
  created_at: string;
}

interface ReviewMetric {
  id: string;
  focus_id: string;
  metric_name: string;
  metric_value: number;
  metric_unit?: string;
  metric_date: string;
  baseline_value?: number;
  target_value?: number;
  current_value?: number;
  category?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface GoalCompetencyMapping {
  id: string;
  goal_id: string;
  competency_id: string;
  focus_id: string;
  created_at: string;
}

interface PerformanceReviewData {
  competencies: PerformanceCompetency[];
  metrics: ReviewMetric[];
  mappings: GoalCompetencyMapping[];
  settings: {
    reviewCycle?: string;
    managerName?: string;
    defaultToReviewView?: boolean;
    competencyFramework?: any;
    goalCompetencyMappings?: any[];
  };
}

const CACHE_KEYS = {
  COMPETENCIES: 'performance_competencies',
  METRICS: 'review_metrics',
  MAPPINGS: 'goal_competency_mappings'
};

export const usePerformanceReview = (focusId?: string) => {
  const [data, setData] = useState<PerformanceReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { getData, setData: setCacheData } = useDataCache();

  useEffect(() => {
    if (!focusId) {
      setData(null);
      setLoading(false);
      return;
    }

    const fetchPerformanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get from cache first
        const cacheKey = `${CACHE_KEYS.COMPETENCIES}_${focusId}`;
        const cachedResult = getData<PerformanceReviewData>(cacheKey);
        if (cachedResult.data) {
          setData(cachedResult.data);
          setLoading(false);
        }

        // Fetch all data in parallel
        const [competenciesResult, metricsResult, mappingsResult, focusResult] = await Promise.all([
          // Fetch competencies
          supabase
            .from('performance_competencies')
            .select('*')
            .eq('focus_id', focusId)
            .order('order_index', { ascending: true }),
          
          // Fetch metrics
          supabase
            .from('review_metrics')
            .select('*')
            .eq('focus_id', focusId)
            .order('metric_date', { ascending: false }),
          
          // Fetch mappings
          supabase
            .from('goal_competency_mappings')
            .select('*')
            .eq('focus_id', focusId),
          
          // Fetch focus settings
          supabase
            .from('user_career_focuses')
            .select('settings')
            .eq('id', focusId)
            .single()
        ]);

        if (competenciesResult.error) throw competenciesResult.error;
        if (metricsResult.error) throw metricsResult.error;
        if (mappingsResult.error) throw mappingsResult.error;
        if (focusResult.error) throw focusResult.error;

        const performanceData: PerformanceReviewData = {
          competencies: competenciesResult.data || [],
          metrics: metricsResult.data || [],
          mappings: mappingsResult.data || [],
          settings: focusResult.data?.settings || {}
        };

        setData(performanceData);
        await setCacheData(cacheKey, performanceData);
        logger.info('Performance review data loaded', { 
          competencies: performanceData.competencies.length,
          metrics: performanceData.metrics.length,
          mappings: performanceData.mappings.length
        });
      } catch (err) {
        logger.error('Failed to fetch performance review data', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, [focusId, getData, setCacheData]);

  const refreshData = async () => {
    if (!focusId) return;
    
    // Clear cache and refetch
    const cacheKey = `${CACHE_KEYS.COMPETENCIES}_${focusId}`;
    await setCacheData(cacheKey, null);
    
    // Re-run the effect by changing state
    setLoading(true);
    setError(null);
    
    // Fetch again
    const fetchPerformanceData = async () => {
      try {
        const [competenciesResult, metricsResult, mappingsResult, focusResult] = await Promise.all([
          supabase
            .from('performance_competencies')
            .select('*')
            .eq('focus_id', focusId)
            .order('order_index', { ascending: true }),
          
          supabase
            .from('review_metrics')
            .select('*')
            .eq('focus_id', focusId)
            .order('metric_date', { ascending: false }),
          
          supabase
            .from('goal_competency_mappings')
            .select('*')
            .eq('focus_id', focusId),
          
          supabase
            .from('user_career_focuses')
            .select('settings')
            .eq('id', focusId)
            .single()
        ]);

        if (competenciesResult.error) throw competenciesResult.error;
        if (metricsResult.error) throw metricsResult.error;
        if (mappingsResult.error) throw mappingsResult.error;
        if (focusResult.error) throw focusResult.error;

        const performanceData: PerformanceReviewData = {
          competencies: competenciesResult.data || [],
          metrics: metricsResult.data || [],
          mappings: mappingsResult.data || [],
          settings: focusResult.data?.settings || {}
        };

        setData(performanceData);
        await setCacheData(cacheKey, performanceData);
      } catch (err) {
        logger.error('Failed to refresh performance review data', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    await fetchPerformanceData();
  };

  return {
    data,
    loading,
    error,
    refreshData,
    competencies: data?.competencies || [],
    metrics: data?.metrics || [],
    mappings: data?.mappings || [],
    settings: data?.settings || {}
  };
};

// Hook to calculate performance phase (similar to internship phases)
export const usePerformancePhase = (focus: any) => {
  const [currentPhase, setCurrentPhase] = useState<string>('planning');
  const [monthsElapsed, setMonthsElapsed] = useState(0);
  const [totalMonths, setTotalMonths] = useState(12);

  useEffect(() => {
    if (!focus?.start_date || !focus?.end_date) return;

    const startDate = new Date(focus.start_date);
    const endDate = new Date(focus.end_date);
    const today = new Date();

    // Calculate total months
    const totalMs = endDate.getTime() - startDate.getTime();
    const totalDays = totalMs / (1000 * 60 * 60 * 24);
    const calculatedTotalMonths = Math.ceil(totalDays / 30);
    setTotalMonths(calculatedTotalMonths);

    // Calculate elapsed months
    const elapsedMs = today.getTime() - startDate.getTime();
    const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
    const calculatedMonthsElapsed = Math.floor(elapsedDays / 30);
    setMonthsElapsed(calculatedMonthsElapsed);

    // Determine phase based on months elapsed
    let phase = 'planning';
    if (calculatedMonthsElapsed <= 2) {
      phase = 'planning';
    } else if (calculatedMonthsElapsed <= 4) {
      phase = 'foundation';
    } else if (calculatedMonthsElapsed <= 8) {
      phase = 'execution';
    } else if (calculatedMonthsElapsed <= 10) {
      phase = 'impact';
    } else {
      phase = 'review_prep';
    }

    setCurrentPhase(phase);
  }, [focus]);

  return { currentPhase, monthsElapsed, totalMonths };
};